import type {Repository} from '@github-ui/current-repository'
import {
  actionsWorkflowRunPath,
  branchPath,
  commitHovercardPath,
  repoAttestationDownloadPath,
  repoAttestationShowPath,
  repoAttestationsIndexPath,
} from '@github-ui/paths'
import {useClientValue} from '@github-ui/use-client-value'
import {useNavigate} from '@github-ui/use-navigate'
import {DownloadIcon, VerifiedIcon} from '@primer/octicons-react'
import {Box, Heading, IconButton, Link, Octicon, Pagination, RelativeTime, Text, Truncate} from '@primer/react'
import {DataTable} from '@primer/react/drafts'

import type {Attestation, PageInfo} from '../types'
import {VisuallyHidden} from './VisuallyHidden'

type Pagination = {
  before?: string
  after?: string
}

type AttestationsTableProps = {
  repo: Repository
  attestations: Attestation[]
  pageInfo?: PageInfo
}

export function AttestationsTable({repo, attestations, pageInfo}: AttestationsTableProps) {
  const defaultPageState = {
    pageCount: 1,
    currentPage: 1,
  }
  // Update the pagination state when pageInfo changes (only runs client-side)
  const [pageState] = useClientValue(
    (): {pageCount: number; currentPage: number} => {
      // Don't attempt to compute pagination if no attestations exist for this repo
      if (!pageInfo) {
        return defaultPageState
      }

      const params = new URLSearchParams(window.location.search)
      let currentPage = 1
      let pageCount = 1
      // Note: The attestations API only tells us if there's a "next" page when paginating forwards, and a "previous"
      // page when paginating backwards so we presume that there is always a next page when paginating
      // backwards and a previous page when paginating forwards.

      // If there is a next page or we are paginating "backwards", we need at least 2 pages and the current page to be 1
      // for the next button to show
      if (pageInfo.hasNextPage || params.get('before')) {
        pageCount += 1
      }
      // If there is a previous page or we are paginating "forwards", we need at least 2 pages and the current
      // page to be 2 for the previous button to show
      if (pageInfo.hasPreviousPage || params.get('after')) {
        pageCount += 1
        currentPage = 2
      }

      return {
        pageCount,
        currentPage,
      }
    },
    defaultPageState,
    [pageInfo],
  )

  const navigate = useNavigate()

  if (attestations.length === 0) {
    return <BlankSlate />
  }

  return (
    <Box>
      <DataTable
        data={attestations}
        columns={[
          {
            header: 'Artifact',
            field: 'subjectName',
            width: 'grow',
            rowHeader: true,
            renderCell: row => {
              return (
                <Link
                  href={repoAttestationShowPath({
                    repo,
                    attestationId: row.id,
                  })}
                  sx={{color: 'fg.default'}}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <VerifiedIcon size={16} className={'mr-2 color-fg-muted'} verticalAlign={'middle'} />
                      <Text>{row.subjectName || 'Untitled Subject'}</Text>
                    </Box>
                  </Box>
                </Link>
              )
            },
          },
          {
            header: 'Workflow Run',
            id: 'runInvocationUri',
            width: '250px',
            rowHeader: true,
            renderCell: row => {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const [_, runId, attempt] = row.runInvocationUri.match(/\/actions\/runs\/(\d+)\/attempts\/(\d+)$/) || []
              return (
                <Link
                  href={actionsWorkflowRunPath({
                    repo: repo.name,
                    owner: repo.ownerLogin,
                    runId,
                    attempt,
                  })}
                  data-testid="attestation-workflow-link"
                  sx={{color: 'fg.default'}}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontWeight: 'normal',
                      }}
                    >
                      <Truncate title={row.buildConfigDisplayName} inline={true} sx={{maxWidth: 250, pr: 3}}>
                        <Text>
                          {row.buildConfigDisplayName.replace(/^\.github\/workflows\//, '')}@{row.sourceRepositoryRef}
                        </Text>
                      </Truncate>
                    </Box>
                  </Box>
                </Link>
              )
            },
          },
          {
            header: 'Created',
            field: 'createdAt',
            width: '120px',
            rowHeader: true,
            renderCell: row => {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    justifyContent: 'center',
                  }}
                >
                  <Text sx={{color: 'fg.default', fontWeight: 'normal'}}>
                    <RelativeTime data-testid="attestation-relative-time" tense="past" datetime={row.createdAt}>
                      {new Date(Date.parse(row.createdAt)).toLocaleDateString('default', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </RelativeTime>
                  </Text>
                </Box>
              )
            },
          },
          {
            header: 'Commit',
            id: 'sourceRepositoryDigest',
            width: '100px',
            renderCell: row => {
              return (
                <Link
                  href={branchPath({
                    owner: repo.ownerLogin,
                    repo: repo.name,
                    branch: row.sourceRepositoryDigest,
                  })}
                  data-testid="attestation-commit-link"
                  sx={{color: 'fg.default', fontFamily: 'monospace', fontWeight: 'normal'}}
                  data-hovercard-url={commitHovercardPath({
                    owner: repo.ownerLogin,
                    repo: repo.name,
                    commitish: row.sourceRepositoryDigest,
                  })}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      justifyContent: 'center',
                      pr: 2,
                    }}
                  >
                    <Text>{row.sourceRepositoryDigest.slice(0, 7)}</Text>
                  </Box>
                </Link>
              )
            },
          },
          {
            id: 'actions',
            maxWidth: 60,
            header: () => <VisuallyHidden>Actions</VisuallyHidden>,
            renderCell: row => {
              return (
                // eslint-disable-next-line primer-react/a11y-remove-disable-tooltip
                <IconButton
                  unsafeDisableTooltip={true}
                  aria-label="Download Sigstore bundle"
                  icon={DownloadIcon}
                  data-turbo="false"
                  target="_blank"
                  data-testid="attestation-download-link"
                  variant="invisible"
                  as="a"
                  href={repoAttestationDownloadPath({
                    repo,
                    attestationId: row.id,
                  })}
                />
              )
            },
          },
        ]}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          px: 3,
          py: '10px',
          borderBottomWidth: 1,
          borderRightWidth: 1,
          borderLeftWidth: 1,
          borderTopWidth: 0,
          borderStyle: 'solid',
          borderColor: 'border.default',
          borderEndStartRadius: 6,
          borderEndEndRadius: 6,
          // Override the margin of the Pagination component
          '> nav': {
            margin: 0,
          },
        }}
      >
        <Pagination
          pageCount={pageState.pageCount}
          currentPage={pageState.currentPage}
          marginPageCount={1}
          onPageChange={(e, pageIndex) => {
            e.preventDefault()
            if (!pageInfo) {
              return
            }
            const pagination: Pagination = {}
            // Translate the "current" next/previous page to the end/start cursor
            if (pageIndex > pageState.currentPage) {
              pagination.after = pageInfo.endCursor
            } else if (pageIndex < pageState.currentPage) {
              pagination.before = pageInfo.startCursor
            }

            navigate(
              repoAttestationsIndexPath({
                repo,
                pagination,
              }),
            )
          }}
          showPages={false}
        />
      </Box>
    </Box>
  )
}

const BlankSlate = () => {
  return (
    <Box
      sx={{
        margin: '0 auto',
        borderColor: 'border.default',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 6,
      }}
    >
      <Box className="blankslate">
        <Octicon icon={VerifiedIcon} size={30} sx={{color: 'fg.subtle', mb: 3}} />
        <Heading as="h2" className="blankslate-heading" sx={{fontSize: 4, mb: 2}}>
          No attestations
        </Heading>
        <p>
          Create attestations using the{' '}
          <Link inline href="https://github.com/actions/attest-build-provenance" target="_blank">
            attest-build-provenance
          </Link>{' '}
          action
        </p>
      </Box>
    </Box>
  )
}

try{ AttestationsTable.displayName ||= 'AttestationsTable' } catch {}
try{ BlankSlate.displayName ||= 'BlankSlate' } catch {}